<template>
  <el-dialog
    :visible="visible"
    width="40%"
    :close-on-click-modal="false"
    @close="handleClose"
    @open="focusToken"
  >
    <ContentHeader
      slot="title"
      :title="$t('stability.matches.addTitle')"
    />
    <!-- No time point, no match: the pairing needs one to point at -->
    <el-alert
      v-if="!hasTimePoints"
      :title="$t('stability.timePoints.defineFirst')"
      type="warning"
      :closable="false"
      show-icon
    />

    <ValidationObserver
      v-else
      ref="observer"
    >
      <el-form
        label-position="top"
        @submit.native.prevent="submit"
      >
        <FormItem
          rules="required"
          :label="$t('stability.timePoints.pick')"
        >
          <el-select
            ref="timePointSelect"
            v-model="form.stabilityTimePointId"
            :placeholder="$t('stability.timePoints.pick')"
          >
            <el-option
              v-for="tp in timePoints"
              :key="tp.id"
              :value="tp.id"
              :label="timePointLabel(tp)"
            />
          </el-select>
        </FormItem>

        <FormItem
          rules="required"
          :label="$t('stability.temperature')"
        >
          <el-input-number
            v-model="form.temperature"
            size="mini"
            :min="-100"
            :max="300"
          />
        </FormItem>

        <FormItem :label="$t('stability.humidity') + ' - %RH'">
          <el-row
            type="flex"
            align="middle"
            :gutter="12"
          >
            <el-col :span="8">
              <el-switch
                v-model="form.humidityEnabled"
                :active-text="$t('stability.includeHumidity')"
              />
            </el-col>
            <el-col
              v-if="form.humidityEnabled"
              :span="16"
            >
              <FormItem
                :rules="{ required: true, integer: true, between: [0, 100] }"
                margin-bottom-size="none"
              >
                <el-input-number
                  v-model="form.humidity"
                  size="mini"
                  :min="0"
                  :max="100"
                  :step="1"
                />
              </FormItem>
            </el-col>
          </el-row>
        </FormItem>

        <FormItem :label="$t('stability.matrix')">
          <el-input
            v-model="form.matrix"
            maxlength="200"
            :placeholder="$t('stability.matrixPlaceholder')"
          />
        </FormItem>
      </el-form>
    </ValidationObserver>

    <span slot="footer">
      <el-button
        :disabled="loading"
        @click="handleClose"
      >
        {{ $t('global.cancel') }}
      </el-button>
      <el-button
        type="primary"
        :disabled="!hasTimePoints"
        :loading="loading"
        @click="submit"
      >
        {{ $t('global.confirm') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
  import { stabilityApi } from '@/api';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import resolveConditionId from './resolveCondition';

  export default {
    name: 'MatchDialog',
    components: { FormItem, ContentHeader },
    props: {
      visible: { type: Boolean, required: true },
      configId: { type: Number, required: true },
      // The time points of this configuration, the only ones a match may pair
      timePoints: { type: Array, default: () => [] },
      // Already defined conditions: an identical one is reused, not duplicated
      conditions: { type: Array, default: () => [] },
    },
    data() {
      return {
        loading: false,
        form: {
          stabilityTimePointId: null,
          temperature: 30,
          humidity: null,
          humidityEnabled: false,
          matrix: '',
        },
      };
    },

    computed: {
      hasTimePoints() {
        return this.timePoints.length > 0;
      },
    },
    watch: {
      // eslint-disable-next-line object-shorthand
      'form.humidityEnabled'(enabled) {
        if (!enabled) this.form.humidity = null;
      },
    },
    methods: {
      timePointLabel(timePoint) {
        return timePoint.description
          ? `${timePoint.token} — ${timePoint.description}`
          : timePoint.token;
      },
      focusToken() {
        this.$nextTick(
          () => this.$refs.timePointSelect && this.$refs.timePointSelect.focus()
        );
      },
      handleClose() {
        this.$emit('update:visible', false);
        this.form = {
          stabilityTimePointId: null,
          temperature: 30,
          humidity: null,
          humidityEnabled: false,
          matrix: '',
        };
        if (this.$refs.observer) this.$refs.observer.reset();
      },
      async submit() {
        // Without a time point the form is not rendered, so neither is the observer
        if (!this.hasTimePoints) return;
        const valid = await this.$refs.observer.validate();
        if (!valid) return;
        this.loading = true;
        try {
          // Reuses an identical condition instead of duplicating it
          const stabilityConditionId = await resolveConditionId(
            this.configId,
            this.conditions,
            {
              temperature: this.form.temperature,
              humidity: this.form.humidityEnabled ? this.form.humidity : null,
              matrix: this.form.matrix,
            }
          );
          await stabilityApi.createMatch(this.configId, {
            stabilityTimePointId: this.form.stabilityTimePointId,
            stabilityConditionId,
          });
          this.$emit('created');
          this.handleClose();
        } catch (err) {
          this.$notify({
            type: 'error',
            showClose: true,
            message:
              (err.response &&
                err.response.data &&
                err.response.data.error &&
                err.response.data.error.message) ||
              this.$t('stability.errors.generic'),
          });
        } finally {
          this.loading = false;
        }
      },
    },
  };
</script>
